import * as React from 'react';
import { I18n } from 'react-redux-i18n';
import { Modal, ModalFooter, ModalTitle, ModalBody, ModalHeader } from '@common/components/modal';
import last from 'lodash/last';

import { Button } from '@common/components/Button';
import { formatDate, parseDate } from '@common/utils/date';
import Dropdown from '@common/containers/fields/Dropdown';
import { Flex } from '@common/components/Flex';
import { isString, isNumber } from '@common/utils/string';
import { Input } from './components/input';
import { Checkbox } from './components/checkbox';
import { Date } from './components/date';
import { Dropdown as FilterDropdown } from './components/dropdown';
import { TABLE_FIELD_FILTER_TYPE } from '../../Table.constants';

import './Filters.styles.scss';

const FILTER_DTO_PROPERTIES = {
	whereClause1: 'whereClause1',
	operator1: 'operator1',
	operator1: 'operator1',
	whereClause2: 'whereClause2',
	operator2: 'operator2',
};

export class Filters extends React.Component {
	andORPropertyName = 'andOR';
	static defaultProps = {
		defaultValue: {},
		type: TABLE_FIELD_FILTER_TYPE.text,
		outputDateFormat: 'MM/DD/YYYY',
	};

	state = {
		value: this.props.defaultValue,
	};

	processors = {
		[TABLE_FIELD_FILTER_TYPE.date]: (x) => {
			if (x) {
				return formatDate(parseDate(x, this.props.outputDateFormat), { format: 'YYYY-MM-DD' });
			}

			return x;
		}
	};

	normalizeNumberBeforeSave = (value) => {
    if (last(value) === '.') {
      value = value.trim().slice(0, -1);
    }
  
    return value;
  }

  handleApply = () => {
		const { onApply } = this.props;
		const defaultAndORValue = undefined;
		const processedValue = this.state.value
			? {
				[this.andORPropertyName]: defaultAndORValue,
				...this.state.value,
			}
			: undefined;

		onApply(processedValue);
	}

	renderFilterControls = ({ typeProperty, textProperty }) => {
		const { filters, type } = this.props;
		const { value } = this.state;
		const filterTypeValue = filters[value[typeProperty]];
		const processor = this.processors[type] || ((x) => x);
		const currentFilterValue = processor(value[textProperty] ?? '');

		return (
			<Flex alignItems="center" justify="space-between">
				<Dropdown
					className="TableFilters__filter-type"
					id="lbx-grid-filter-operator"
					selectedValue={filterTypeValue ?? ''}
					searchable={false}
					options={Object.values(filters)}
					optionsHeight={100}
					onValueChanged={this.handleFilterTypeChange(typeProperty)}
				/>

				{this.renderField(
					type,
					{
						className: "TableFilters__filter-text",
						value: currentFilterValue,
						metadata: {
							property: textProperty,
						},
						onSave: this.handleFilterChange,
					}
				)}
			</Flex>
		);
	}

	handleFilterTypeChange = (property) => (filterType) => {
		const { filters } = this.props;

		const filterTypeId = Object.keys(filters).find((id) => {
			return filters[id] === filterType;
		});

		this.onChange(property, filterTypeId);
	}

	handleFilterChange = ({ value, metadata: { property } }) => {
		if (this.props.type === TABLE_FIELD_FILTER_TYPE.date) {
			value = formatDate(value, { format: this.props.outputDateFormat });
		}

		this.onChange(property, value);
	}

	onChange = (property, value) => {
		this.setState((state) => ({
			...state,
			value: {
				...state.value,
				[property]: value
			}
		}));
	}

	handleApplyTypeChange = (e) => {
		e.persist();

		const { value } = e.target;

		this.onChange(this.andORPropertyName, value);
	}

	get applyTypes() {
		return [
			{ label: 'And', id: '0' },
			{ label: 'Or', id: '1' },
		];
	}

	getFieldConfig = ({ type, options }) => {
		if (options?.length && options.length > 0 && type != TABLE_FIELD_FILTER_TYPE.checkbox) {
			return {
				render: this.renderDropdown,
			};
		}

		const config = {
			[TABLE_FIELD_FILTER_TYPE.text]: {
				render: this.renderInput
			},
			[TABLE_FIELD_FILTER_TYPE.date]: {
				render: this.renderDate
			},
			[TABLE_FIELD_FILTER_TYPE.number]: {
				render: this.renderNumber
			},
			[TABLE_FIELD_FILTER_TYPE.integer]: {
				render: this.renderInteger
			},
			[TABLE_FIELD_FILTER_TYPE.checkbox]: {
				render: this.renderCheckbox
			},
			[TABLE_FIELD_FILTER_TYPE.dropdown]: {
				render: this.renderDropdown
			},
			[TABLE_FIELD_FILTER_TYPE.image]: {
				render: this.renderInput
			},
			[TABLE_FIELD_FILTER_TYPE.lookupTable]: {
				render: this.renderInput
			},
			[TABLE_FIELD_FILTER_TYPE.table]: {
				render: this.renderInput
			},
			[TABLE_FIELD_FILTER_TYPE.computed]: {
				render: this.renderInput
			},
			[TABLE_FIELD_FILTER_TYPE.summary]: {
				render: this.renderInput
			},
			[TABLE_FIELD_FILTER_TYPE.tableSummary]: {
				render: this.renderInput
			},
			[TABLE_FIELD_FILTER_TYPE.hyperlink]: {
				render: this.renderInput
			},
		};

		return config[type];
	}

	renderNumber = (data) => {
    return (
      <Input
        normalizeBeforeSave={this.normalizeNumberBeforeSave}
        validate={isNumber}
				type="number"
        {...data}
      />
    );
  }

	isInteger = (v) => {
		return Number.isInteger(Number(v));
	}

	renderInteger = (data) => {
    return (
      <Input
        normalizeBeforeSave={this.normalizeNumberBeforeSave}
        validate={this.isInteger}
				type="number"
        {...data}
      />
    );
  }

  renderInput = (data) => {
    return <Input {...data} />;
  }

  renderDate = (data) => {
    return <Date {...data} />;
  }

  renderCheckbox = (data) => {
    return <Checkbox {...data} />;
  }

  renderDropdown = (data) => {
    return <FilterDropdown options={this.props.options} {...data} />;
  }

  renderField = (fieldType, data) => {
    const fieldConfig = this.getFieldConfig({ type: fieldType, options: this.props.options });
    const render = fieldConfig ? fieldConfig.render : (() => null);

    return render({ type: fieldType, ...data });
  }

	componentDidMount() {
		if (!this.props.moduleName) {
			console.error('React Table: provide moduleName property for unique identifiers');
		}
	}
 
  render() {
    const { onClose, filterName } = this.props;
		const { value } = this.state;
		
    return (
      <Modal 
        show
        animation={false}
        backdrop="static"
        bsClass="bootstrap modal"
        dialogClassName="TableFilters"
        onEscapeKeyDown={onClose}
        onHide={onClose}
      >
        <ModalHeader closeButton onHide={onClose}>
          <ModalTitle id="msg-box-header">
            <span className="fa fa-filter" /> {I18n.t('Filter Column')}
          </ModalTitle>
        </ModalHeader>

        <ModalBody>
          <Flex className="TableFilters__main-control" justify="space-between" width="100%" alignItems="center">
						<span>{I18n.t('Show rows where')}{(isNumber(filterName) || isString(filterName)) ? ` ${filterName}` : ''}:</span>

						<Button id="btn-grid-filter-save" className="btn-primary" onClick={this.handleApply}>
							{I18n.t('Filter')}
						</Button>
          </Flex>

					{this.renderFilterControls(
						{
							textProperty: FILTER_DTO_PROPERTIES.whereClause1,
							typeProperty: FILTER_DTO_PROPERTIES.operator1,
						}
					)}

					<Flex alignItems="center" className="TableFilters__filter-apply-types">
						{this.applyTypes.map((type) => (
							<React.Fragment key={type.id}>
								<input 
									name="Table-apply-type"
									type="radio" 
									id={`Table-filter-type-${type.id}`} 
									className="TableFilters__filter-apply-types__input"
									value={type.id}
									checked={type.id === value.andOR}
									onChange={this.handleApplyTypeChange} 
								/>
								<label 
									htmlFor={`Table-filter-type-${type.id}`} 
									className="TableFilters__filter-apply-types__label"
								>
									{I18n.t(type.label)}
								</label>
							</React.Fragment>
						))}
					</Flex>

					{this.renderFilterControls(
						{
							textProperty: FILTER_DTO_PROPERTIES.whereClause2,
							typeProperty: FILTER_DTO_PROPERTIES.operator2,
						}
					)}
        </ModalBody>
      </Modal>
    );
  }
}
